import React from 'react'
import {
  ThemeProvider,
  lightTheme,
  Tooltip,
  Typography
} from '@rodeo-dev/legos'
import styled from 'styled-components'
import IntlMessage from '../../../../common/common/intl/intl_message.jsx'
import currency from '../../../../lib/currency.jsx'
import ExpenseDetails from './expense-details'
import TagLabel from './tag-label.js'
import { PO_STATUSES } from '../../../../../shared/constants'
import IntlUtil from '../../../../lib/intlutil.jsx'
import colors from '@rodeo-dev/legos/dist/themes/colors'
import media from '../../../../theme/media'

const { palette } = lightTheme

const mapPoStatusToStyle = {
  [PO_STATUSES.closed]: {
    backgroundColor: palette.primary.dark,
    color: palette.primary.contrastText
  },
  [PO_STATUSES.open]: {
    backgroundColor: palette.primary.main,
    color: palette.primary.contrastText
  },
  [PO_STATUSES.draft]: {
    backgroundColor: palette.default.main,
    color: palette.primary.contrastText
  }
}

const ExpensePurchaseOrders = ({
  item,
  purchaseOrders,
  onDeleteExpenseSupplier,
  onClickPoButton,
  poDownloadBaseUrl,
  onCreatePurchaseOrder,
  onAddExpense,
  onClickExpenseInvoiceTag,
  hasDate
}) => {
  if (purchaseOrders && purchaseOrders.length > 0) {
    return purchaseOrders.map((poItem, idx) => {
      return (
        <ThemeProvider theme={lightTheme} key={idx}>
          <SupplierInfo hasDate={hasDate}>
            <SupplierContent>
              {!poItem.poNumber && poItem?.expenses?.length <= 0 && (
                <ItemClose
                  type='button'
                  className='item-close'
                  onClick={() =>
                    onDeleteExpenseSupplier(poItem.contactId, poItem.supplierId, item)}
                >
                  <i className='icon icon-phase-close' />
                </ItemClose>
              )}
              <SupplierHeader>
                <SupplierName>{poItem.supplierName}</SupplierName>
                {poItem.remaingPoAmount > 0 && (
                  <RemainingPo hasDate={hasDate}>
                    <span className='label'>
                      <IntlMessage ident='project.viewBudget.remainingPoAmount' />
                    </span>
                    {currency.formatSymbol(poItem.remaingPoAmount)}
                  </RemainingPo>
                )}
              </SupplierHeader>
              <FlexRow>
                <PoWrap>
                  {poItem.purchaseOrderId ? (
                    <LabelWrap>
                      <Tooltip
                        placement='right-start'
                        variant='dark'
                        title={
                          <Typography variant='subtitle2'>
                            {IntlUtil.convertMessage(
                              `common.status.${poItem.status.toLowerCase()}`
                            )}
                          </Typography>
                        }
                      >
                        <TagLabel
                          type='button'
                          onClick={() =>
                            onClickPoButton(poItem.purchaseOrderId)}
                          backgroundColor={
                            mapPoStatusToStyle?.[poItem.status].backgroundColor
                          }
                          color={mapPoStatusToStyle?.[poItem.status].color}
                        >
                          {poItem.poNumber ? (
                            poItem.poNumber
                          ) : (
                            <IntlMessage ident='common.status.draft' />
                          )}
                        </TagLabel>
                      </Tooltip>
                      <Attachment
                        target='_blank'
                        rel='noopener noreferrer'
                        href={encodeURI(
                          `${poDownloadBaseUrl}/${poItem.purchaseOrderId}/${poItem.poNumber}.pdf`
                        )}
                      >
                        <i className='icon icon-phase-attachment-hover' />
                      </Attachment>
                    </LabelWrap>
                  ) : (
                    <AddAction
                      type='button'
                      onClick={() => onCreatePurchaseOrder(poItem)}
                    >
                      <IntlMessage ident='project.form.addPo' />
                    </AddAction>
                  )}
                  <AddAction
                    type='button'
                    onClick={() => onAddExpense(item, poItem)}
                  >
                    <IntlMessage ident='project.viewBudget.addExpense' />
                  </AddAction>
                </PoWrap>
              </FlexRow>
              <ExpenseDetails
                poItem={poItem}
                onClickExpenseInvoiceTag={onClickExpenseInvoiceTag}
                hasDate={hasDate}
              />
            </SupplierContent>
            <SplitterBottom />
          </SupplierInfo>
        </ThemeProvider>
      )
    })
  }

  return ''
}

const SupplierInfo = styled.div`
  position: relative;
  margin: 0 auto;
  background: #f7f7f7;
  width: 100%;
  padding: 0 20px;
  padding-left: ${({ hasDate }) => (hasDate ? 'calc(16% + -1px)' : '20px')};

  ${media.desktop`
  width: calc(100% - 24px);
  `}
`

const SupplierContent = styled.div`
  position: relative;
  display: block;
  padding: 15px 20px 5px 30px;
  width: calc(100% - 24px);

  &:hover {
    .item-close {
      display: block;
    }
  }
`

const ItemClose = styled.button`
  background: none;
  border: none;
  left: 3px;
  top: 11px;
  position: absolute;
  display: none;

  .icon {
    display: block;
    width: 16px;
    height: 16px;
  }
`

const AddAction = styled.button`
  background: none;
  border: none;
  color: ${colors.default.lightMain};
  font-size: 13px;
  text-align: right;
  padding: 0;
  margin-right: 5px;

  &:hover {
    color: ${palette.primary.main};
  }
`

const PoWrap = styled.div`
  align-items: center;
  display: flex;
`

const SupplierHeader = styled.div`
  display: flex;
  margin-bottom: 5px;
`

const SupplierName = styled.div`
  display: block;
  color: #000000;
  font-size: 13px;
`

export const LabelWrap = styled.span`
  color: ${colors.text.secondary};
  font-size: 13px;
  height: 100%;
  width: auto;
  display: flex;

  & .icon {
    width: 16px;
    height: 16px;
    display: block;
  }
`

export const Attachment = styled.a`
  display: block;
  width: 16px;
  margin-right: 15px;
`

const RemainingPo = styled.div`
  position: absolute;
  right: ${({ hasDate }) => (hasDate ? '32.7%' : '27.6%')};
  font-size: 13px;

  > .label {
    color: ${colors.text.secondary};
    margin-right: 5px;
  }
`

export const FlexRow = styled.div`
  display: flex;
  flex-direction: column;
  margin: 15px 0;
`

const SplitterBottom = styled.div`
  bottom: 0;
  display: block;
  position: absolute;
  left: auto;
  right: 0;
  width: 100%;
  height: 1px;
  top: auto;
  background-color: #efefef;
  width: 100%;
`

export default ExpensePurchaseOrders
